import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { kpiData, industries } from '../data/mockData';
import { ArrowRight, Target, BarChart2, TrendingUp, CheckCircle, Scale, Award } from 'lucide-react';
import { Button } from '../components/ui/button';

const BenchmarkIntelligencePage = () => {
  return (
    <div className="min-h-screen">
      <Navbar />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full filter blur-3xl" />
            <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-emerald-500/10 rounded-full filter blur-3xl" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Benchmark Intelligence:
                <span className="block bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">
                  Industry Comparison & Competitive Analysis
                </span>
              </h1>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Compare your organization's performance with industry averages and top performers to identify opportunities for improvement.
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Know Where You Stand. See Where You Can Go.
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Benchmark Intelligence provides comprehensive comparison of your organization's KPIs against industry averages and top performers in your sector. Using AI-driven analysis of publicly available data, we help CXOs understand their competitive position and identify high-impact improvement opportunities.
                </p>

                <div className="space-y-4">
                  {[
                    'Compare against industry average benchmarks',
                    'Analyze top performer metrics in your sector',
                    'Identify performance gaps and opportunities',
                    'Track progress over time with trend analysis',
                    'Sector-specific KPI relevance scoring',
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Supported Industries</h3>
                <div className="grid grid-cols-2 gap-3">
                  {industries.map((industry, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-white rounded-lg shadow-sm">
                      <Target className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm text-gray-700">{industry}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benchmark Visualization */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Visual Benchmark Comparison</h2>
              <p className="text-gray-600">See how your organization stacks up against the competition</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {kpiData.slice(0, 6).map((kpi, index) => {
                const yourPercent = (kpi.yourValue / kpi.topPerformer) * 100;
                const avgPercent = (kpi.industryAvg / kpi.topPerformer) * 100;
                
                return (
                  <div key={index} className="bg-white rounded-xl p-6 shadow-lg">
                    <h4 className="font-semibold text-gray-900 mb-4">{kpi.name}</h4>
                    
                    <div className="space-y-3">
                      {/* Your Organization */}
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">Your Org</span>
                          <span className="font-medium text-emerald-600">{kpi.yourValue}{kpi.unit === '%' ? '%' : ''}</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-emerald-500 rounded-full transition-all duration-1000"
                            style={{ width: `${Math.min(yourPercent, 100)}%` }}
                          />
                        </div>
                      </div>

                      {/* Industry Average */}
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">Industry Avg</span>
                          <span className="font-medium text-gray-500">{kpi.industryAvg}{kpi.unit === '%' ? '%' : ''}</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gray-400 rounded-full"
                            style={{ width: `${Math.min(avgPercent, 100)}%` }}
                          />
                        </div>
                      </div>

                      {/* Top Performer */}
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">Top Performer</span>
                          <span className="font-medium text-yellow-600">{kpi.topPerformer}{kpi.unit === '%' ? '%' : ''}</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-yellow-500 rounded-full w-full" />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">How Benchmark Intelligence Works</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: BarChart2,
                  title: 'Data Collection',
                  description: 'We aggregate data from public filings, industry reports, and verified sources to create comprehensive benchmarks.',
                },
                {
                  icon: Scale,
                  title: 'AI Analysis',
                  description: 'Our Agentic AI processes and normalizes data to ensure accurate, apples-to-apples comparisons across organizations.',
                },
                {
                  icon: Award,
                  title: 'Actionable Insights',
                  description: 'Receive detailed reports highlighting gaps, opportunities, and strategic recommendations for improvement.',
                },
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <step.icon className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-emerald-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">
              Ready to See How You Compare?
            </h2>
            <p className="text-emerald-100 text-lg mb-8">
              Start your free trial and get instant access to industry benchmarks for your sector.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/dashboard">
                <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 w-full sm:w-auto">
                  Start Assessment
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-emerald-700 w-full sm:w-auto">
                  Schedule Demo
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BenchmarkIntelligencePage;
